import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  getFabricOrdersForProcurement,
  getFabricOrderDetail,
  getSuppliers,
  seedDefaultSuppliers,
  inlineUpdateFabricOrder,
  bulkUpdateFabricStatus,
  groupFabricOrders,
  ungroupFabricOrders,
} from '@/lib/api';
import {
  Search,
  Package,
  Calendar as CalendarIcon,
  X,
  Eye,
  Calculator,
  Mail,
  FileText,
  Link2,
  Unlink,
  Edit,
  ArrowUpDown,
  Filter,
  ChevronDown,
  ChevronRight,
  Copy,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { format } from 'date-fns';
import { formatCustomerNo, extractGarmentSuffix } from '@/lib/displayUtils';
import { sortCustomizations } from '@/lib/customizationUtils';
import FabricCalculatorModal from '@/components/fabrics/FabricCalculatorModal';
import { FABRIC_ORDER_STATUSES, getFabricStatusConfig } from '@/constants/fabricStatuses';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { formatDateWord } from '@/lib/dateFormat';

const API_URL = getApiBaseUrl();

const priorities = [
  { value: 'low', label: 'Low', color: 'bg-blue-600/80 text-white' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-600/80 text-white' },
  { value: 'high', label: 'High', color: 'bg-orange-700/80 text-white' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-900/80 text-white' },
];

const orderStatusBadgeMap = {
  enquiry: { label: 'Enquiry', color: 'bg-slate-600/80 text-white border-slate-600' },
  pending: { label: 'Pending', color: 'bg-amber-600/80 text-white border-amber-600' },
  processing: { label: 'Processing', color: 'bg-blue-600/80 text-white border-blue-600' },
  pending_payment: { label: 'Pending Payment', color: 'bg-orange-500/80 text-white border-orange-500' },
  completed: { label: 'Completed', color: 'bg-emerald-600/80 text-white border-emerald-600' },
  cancelled: { label: 'Cancelled', color: 'bg-red-900/80 text-white border-red-900' },
  refunded: { label: 'Refunded', color: 'bg-gray-700/80 text-white border-gray-700' },
};

const dateTypes = [
  { value: 'order_date', label: 'Order Date' },
  { value: 'dispatched_date', label: 'Dispatch Date' },
  { value: 'received_date', label: 'Received Date' },
];

const DEFAULT_PAGE_SIZE = 60;
const SCROLL_LOAD_THRESHOLD = 220;
const RECEIVED_STATUS_VALUES = ['received_sg', 'received_gobi'];

const getAlterationMeta = (fabric) => {
  const sourceGarmentNo = String(fabric?.source_garment_no || fabric?.garment_no || '').toUpperCase();
  const match = sourceGarmentNo.match(/-ALT(\d+)?$/i);
  const inferredCycle = match ? Number(match[1] || 1) : 0;
  const storedCycle = Number(fabric?.alteration_cycle || 0);
  const isAlteration = Boolean(fabric?.is_alteration) || Boolean(match);
  const cycle = storedCycle > 0 ? storedCycle : (isAlteration ? inferredCycle || 1 : 0);
  return {
    isAlteration,
    cycle,
    label: isAlteration ? (cycle > 1 ? `ALT-${cycle}` : 'ALT') : null,
  };
};

const FabricProcurementPage = () => {
  const [fabricOrders, setFabricOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null); // For collapsible rows

  // Stores list - fetched dynamically based on user access
  const [stores, setStores] = useState([]);

  // Global filters
  const searchInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [storeFilter, setStoreFilter] = useState('all');
  const [dateTypeFilter, setDateTypeFilter] = useState('order_date');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Inline column filters
  const [filters, setFilters] = useState({
    supplier: 'all',
    priority: 'all',
    status: 'not_ordered',
    alterationType: 'all',
  });
  const [quickView, setQuickView] = useState('not_ordered');
  const [fabricDetailsById, setFabricDetailsById] = useState({});
  const [detailsLoadingById, setDetailsLoadingById] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const tableScrollRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const fetchSeqRef = useRef(0);

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Modal states
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedFabricForDetails, setSelectedFabricForDetails] = useState(null);
  const [collationModalOpen, setCollationModalOpen] = useState(false);
  const [groupByParent, setGroupByParent] = useState(true); // Default ON
  const [supplierUpdates, setSupplierUpdates] = useState({}); // Per-supplier date/status updates
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [bulkEditSupplier, setBulkEditSupplier] = useState('');
  const [bulkEditPriority, setBulkEditPriority] = useState('');
  const [bulkEditNotes, setBulkEditNotes] = useState('');
  
  // Customizations modal state
  const [customizationsOpen, setCustomizationsOpen] = useState(false);
  const [selectedFabricForCustomizations, setSelectedFabricForCustomizations] = useState(null);
  
  // Calculator modal state
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [selectedFabricForCalculator, setSelectedFabricForCalculator] = useState(null);
  
  // Date edit modal state
  const [dateEditModalOpen, setDateEditModalOpen] = useState(false);
  const [selectedFabricForDates, setSelectedFabricForDates] = useState(null);
  const [editingDates, setEditingDates] = useState({
    order_date: null,
    dispatched_date: null,
    received_date: null
  });

  // Fetch accessible stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const storesData = await getCachedAccessibleStores();
        setStores(Array.isArray(storesData) ? storesData : []);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };
    fetchStores();
  }, []);

  const fetchSuppliersData = useCallback(async () => {
    try {
      const suppliersRes = await getSuppliers();
      const supplierData = suppliersRes.data || [];
      setSuppliers(supplierData);

      if (supplierData.length === 0) {
        await seedDefaultSuppliers();
        const seededSuppliersRes = await getSuppliers();
        setSuppliers(seededSuppliersRes.data || []);
        toast.success('Default suppliers have been created');
      }
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  }, []);

  useEffect(() => {
    fetchSuppliersData();
  }, [fetchSuppliersData]);

  // Fetch data
  const fetchData = useCallback(async ({ page = 1, append = false } = {}) => {
    const requestId = fetchSeqRef.current + 1;
    fetchSeqRef.current = requestId;

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const searchValue = (debouncedSearch || '').trim();
      const pageSize = searchValue.length >= 2 ? 75 : DEFAULT_PAGE_SIZE;

      const params = {};
      params.page = page;
      params.page_size = pageSize;
      params.lite = true;
      if (storeFilter !== 'all') params.store = storeFilter;
      if (fromDate) params.date_from = format(fromDate, 'yyyy-MM-dd');
      if (toDate) params.date_to = format(toDate, 'yyyy-MM-dd');
      if (dateTypeFilter) params.date_type = dateTypeFilter;
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (filters.supplier !== 'all') params.supplier_id = filters.supplier;
      if (filters.status !== 'all' && filters.status !== 'received') {
        params.status = filters.status;
      }
      if (filters.status === 'received') {
        params.status_in = RECEIVED_STATUS_VALUES.join(',');
      }

      if (searchValue.length >= 2) {
        params.search = searchValue;
      }

      params.lite = true;

      const fabricsRes = await getFabricOrdersForProcurement(params, { signal: abortController.signal });

      if (requestId !== fetchSeqRef.current) {
        return;
      }

      const incomingRows = fabricsRes.data || fabricsRes || [];

      let nextRowsCount = incomingRows.length;
      setFabricOrders((prev) => {
        if (!append) {
          nextRowsCount = incomingRows.length;
          return incomingRows;
        }

        const seen = new Set(prev.map((fabric) => fabric.fabric_id));
        const deduped = incomingRows.filter((fabric) => !seen.has(fabric.fabric_id));
        const merged = [...prev, ...deduped];
        nextRowsCount = merged.length;
        return merged;
      });

      if (!append) {
        setFabricDetailsById({});
        setExpandedRow(null);
        setSelectedFabrics([]);
        if (tableScrollRef.current) {
          tableScrollRef.current.scrollTop = 0;
        }
      }

      setCurrentPage(page);
      setHasMorePages(incomingRows.length === pageSize);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      toast.error('Failed to fetch data');
    } finally {
      if (fetchAbortRef.current === abortController) {
        fetchAbortRef.current = null;
      }
      if (requestId === fetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [storeFilter, fromDate, toDate, dateTypeFilter, debouncedSearch, filters.priority, filters.supplier, filters.status]);

  useEffect(() => {
    fetchData({ page: 1, append: false });
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const handleSlashFocus = (event) => {
      if (event.key !== '/') return;
      const tagName = document.activeElement?.tagName;
      const isTypingContext = tagName === 'INPUT' || tagName === 'TEXTAREA' || document.activeElement?.isContentEditable;
      if (isTypingContext) return;
      event.preventDefault();
      searchInputRef.current?.focus();
    };

    window.addEventListener('keydown', handleSlashFocus);
    return () => window.removeEventListener('keydown', handleSlashFocus);
  }, []);

  // SSE Real-time updates
  useEffect(() => {
    const backendUrl = getApiBaseUrl();
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.warn('⚠️ No auth token found, skipping SSE connection');
      return;
    }
    
    // Create EventSource for SSE
    const sseUrl = `${backendUrl}/api/fabric-orders/stream?token=${token}`;
    const eventSource = new EventSource(sseUrl);
    
    eventSource.addEventListener('connected', (event) => {
      console.log('✅ Connected to fabric orders stream');
    });
    
    eventSource.addEventListener('fabric_update', (event) => {
      try {
        const payload = JSON.parse(event.data);
        const { updates } = payload;
        
        console.log(`📡 Received ${updates.length} fabric update(s) from SSE`);
        
        // Apply updates to local state
        setFabricOrders(prevFabrics => {
          const updatedFabrics = [...prevFabrics];
          
          updates.forEach(update => {
            const { type, data } = update;
            const fabricIndex = updatedFabrics.findIndex(f => f.fabric_id === data.fabric_id);
            
            if (fabricIndex !== -1) {
              // Update existing fabric
              if (type === 'fabric_updated') {
                updatedFabrics[fabricIndex] = {
                  ...updatedFabrics[fabricIndex],
                  ...data.changes
                };
                console.log(`✅ Updated fabric ${data.fabric_id}`);
              } else if (type === 'fabric_status_updated') {
                updatedFabrics[fabricIndex] = {
                  ...updatedFabrics[fabricIndex],
                  fabric_order_status: data.fabric_order_status
                };
                console.log(`✅ Updated fabric status ${data.fabric_id} to ${data.fabric_order_status}`);
              }
            }
          });
          
          return updatedFabrics;
        });
      } catch (error) {
        console.error('❌ Error processing fabric update:', error);
      }
    });
    
    eventSource.onerror = (error) => {
      console.error('❌ SSE connection error:', error);
    };
    
    // Cleanup on unmount
    return () => {
      eventSource.close();
      console.log('🔌 Disconnected from fabric orders stream');
    };
  }, []); // Empty deps - only connect once

  // Get status config (using centralized constants)
  const getStatusConfig = getFabricStatusConfig;

  const getPriorityConfig = (priority) => {
    return priorities.find((p) => p.value === priority) || { label: priority, color: '' };
  };

  const getOrderStatusConfig = (status) => {
    const normalized = String(status || '').toLowerCase();
    return orderStatusBadgeMap[normalized] || {
      label: status || 'Unknown',
      color: 'bg-slate-500/80 text-white border-slate-500',
    };
  };

  const applyQuickView = (view) => {
    setQuickView(view);
    if (view === 'all') {
      setFilters({ supplier: 'all', priority: 'all', status: 'all', alterationType: 'all' });
      return;
    }
    if (view === 'not_ordered') {
      setFilters((prev) => ({ ...prev, status: 'not_ordered' }));
      return;
    }
    if (view === 'checking_with_supplier') {
      setFilters((prev) => ({ ...prev, status: 'checking_with_supplier' }));
      return;
    }
    if (view === 'in_transit') {
      setFilters((prev) => ({ ...prev, status: 'in_transit' }));
      return;
    }
    if (view === 'received') {
      setFilters((prev) => ({ ...prev, status: 'received' }));
      return;
    }
  };

  const handleTableScroll = useCallback((event) => {
    const el = event.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD;

    if (!nearBottom || loading || loadingMore || !hasMorePages) {
      return;
    }

    fetchData({ page: currentPage + 1, append: true });
  }, [loading, loadingMore, hasMorePages, currentPage, fetchData]);

  const ensureFabricDetail = useCallback(async (fabricId) => {
    if (!fabricId || fabricDetailsById[fabricId] || detailsLoadingById[fabricId]) {
      return fabricDetailsById[fabricId] || null;
    }

    setDetailsLoadingById((prev) => ({ ...prev, [fabricId]: true }));
    try {
      const response = await getFabricOrderDetail(fabricId);
      const detail = response.data || {};
      setFabricDetailsById((prev) => ({ ...prev, [fabricId]: detail }));
      setFabricOrders((prev) => prev.map((f) => (f.fabric_id === fabricId ? { ...f, ...detail } : f)));
      return detail;
    } catch (error) {
      toast.error('Failed to load fabric details');
      return null;
    } finally {
      setDetailsLoadingById((prev) => ({ ...prev, [fabricId]: false }));
    }
  }, [fabricDetailsById, detailsLoadingById]);

  // Handle view customizations
  const handleViewCustomizations = async (fabric) => {
    const needsDetail = !fabric?.customizations;
    let resolvedFabric = fabric;
    if (needsDetail && fabric?.fabric_id) {
      const detail = await ensureFabricDetail(fabric.fabric_id);
      if (detail) {
        resolvedFabric = { ...fabric, ...detail };
      }
    }
    setSelectedFabricForCustomizations(resolvedFabric);
    setCustomizationsOpen(true);
  };
  
  // Handle fabric calculator
  const handleCalculator = (fabric) => {
    setSelectedFabricForCalculator(fabric);
    setCalculatorOpen(true);
  };
  
  // Handle date edit modal open
  const handleOpenDateModal = (fabric) => {
    setSelectedFabricForDates(fabric);
    setEditingDates({
      order_date: fabric.order_date || null,
      dispatched_date: fabric.dispatched_date || null,
      received_date: fabric.received_date || null
    });
    setDateEditModalOpen(true);
  };
  
  // Handle date updates from modal
  const handleSaveDates = async () => {
    if (!selectedFabricForDates) return;
    
    try {
      // Update all three dates at once
      await inlineUpdateFabricOrder(selectedFabricForDates.fabric_id, editingDates);
      
      // Update local state
      setFabricOrders((prev) =>
        prev.map((f) => 
          f.fabric_id === selectedFabricForDates.fabric_id 
            ? { ...f, ...editingDates }
            : f
        )
      );
      
      toast.success('Procurement dates updated');
      setDateEditModalOpen(false);
    } catch (error) {
      toast.error('Failed to update dates');
    }
  };
  
  // Handle calculator confirm
  const handleCalculatorConfirm = async (calculatedQty) => {
    if (selectedFabricForCalculator) {
      await handleInlineUpdate(selectedFabricForCalculator.fabric_id, 'quantity', calculatedQty);
      toast.success(`Fabric quantity set to ${calculatedQty}m`);
    }
  };

  // Handle inline field update
  const handleInlineUpdate = async (fabricId, field, value) => {
    try {
      const updateData = { [field]: value };
      await inlineUpdateFabricOrder(fabricId, updateData);
      
      // Update local state
      setFabricOrders((prev) =>
        prev.map((f) => {
          if (f.fabric_id === fabricId) {
            const updated = { ...f, [field]: value };
            if (field === 'supplier_id') {
              const supplier = suppliers.find((s) => s.supplier_id === value);
              updated.supplier_name = supplier?.supplier_name || null;
            }
            return updated;
          }
          return f;
        })
      );
      toast.success('Updated successfully');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Handle selection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedFabrics(filteredFabrics.map((f) => f.fabric_id));
    } else {
      setSelectedFabrics([]);
    }
  };

  const handleSelectFabric = (fabricId, checked) => {
    if (checked) {
      setSelectedFabrics((prev) => [...prev, fabricId]);
    } else {
      setSelectedFabrics((prev) => prev.filter((id) => id !== fabricId));
    }
  };

  // Bulk actions
  const handleBulkStatusUpdate = async () => {
    if (!bulkStatusValue) {
      toast.error('Please select a status');
      return;
    }
    try {
      await bulkUpdateFabricStatus(selectedFabrics, bulkStatusValue);
      toast.success('Status updated for selected items');
      setBulkStatusModalOpen(false);
      setBulkStatusValue('');
      setSelectedFabrics([]);
      await fetchData({ page: 1, append: false });
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleBulkEdit = async () => {
    try {
      const updates = {};
      if (bulkEditSupplier) updates.supplier_id = bulkEditSupplier;
      if (bulkEditPriority) updates.priority = bulkEditPriority;
      if (bulkEditNotes) updates.notes = bulkEditNotes;

      if (Object.keys(updates).length === 0) {
        toast.error('No changes to apply');
        return;
      }

      // Call bulk edit endpoint with proper body format
      const response = await fetch(`${API_URL}/api/fabric-orders/bulk-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          fabric_ids: selectedFabrics,
          updates: updates
        }),
      });
      
      if (!response.ok) throw new Error('Failed to update');
      
      toast.success('Bulk edit applied successfully');
      setBulkEditModalOpen(false);
      setBulkEditSupplier('');
      setBulkEditPriority('');
      setBulkEditNotes('');
      setSelectedFabrics([]);
      await fetchData({ page: 1, append: false });
    } catch (error) {
      toast.error('Failed to apply bulk edit');
    }
  };

  const handleGroup = async () => {
    if (selectedFabrics.length < 2) {
      toast.error('Select at least 2 fabric orders to group');
      return;
    }
    try {
      await groupFabricOrders(selectedFabrics);
      toast.success('Fabric orders grouped');
      setSelectedFabrics([]);
      await fetchData({ page: 1, append: false });
    } catch (error) {
      toast.error('Failed to group');
    }
  };

  const handleUngroup = async () => {
    try {
      await ungroupFabricOrders(selectedFabrics);
      toast.success('Fabric orders ungrouped');
      setSelectedFabrics([]);
      await fetchData({ page: 1, append: false });
    } catch (error) {
      toast.error('Failed to ungroup');
    }
  };

  // View details
  const handleViewDetails = (fabric) => {
    setSelectedFabricForDetails(fabric);
    setDetailsModalOpen(true);
  };

  // Generate collation summary - grouped by SUPPLIER first
  const generateCollationSummary = () => {
    const selectedFabricData = fabricOrders.filter(f => selectedFabrics.includes(f.fabric_id));
    
    const collatedBySupplier = {};
    
    selectedFabricData.forEach(fabric => {
      // Get supplier name (or "Not Updated" if missing)
      const supplierKey = fabric.supplier_name || 'Not Updated';
      
      // Initialize supplier group if not exists
      if (!collatedBySupplier[supplierKey]) {
        collatedBySupplier[supplierKey] = {
          items: {},
          fabricIds: []
        };
      }
      
      // Determine customer/group name
      let customerKey;
      if (groupByParent && fabric.parent_order_number) {
        customerKey = fabric.parent_customer_name || fabric.customer_name;
      } else {
        customerKey = fabric.customer_name;
      }
      
      // Group by fabric SKU within customer
      const fabricSKU = fabric.fabric_sku || 'Unknown SKU';
      const itemKey = `${customerKey}|||${fabricSKU}`; // Use delimiter to split later
      
      if (!collatedBySupplier[supplierKey].items[itemKey]) {
        collatedBySupplier[supplierKey].items[itemKey] = {
          customer: customerKey,
          fabricSKU: fabricSKU,
          totalQty: 0,
          fabricItems: []
        };
      }
      
      // Add quantity and track fabric IDs
      collatedBySupplier[supplierKey].items[itemKey].totalQty += parseFloat(fabric.quantity || 0);
      collatedBySupplier[supplierKey].items[itemKey].fabricItems.push(fabric);
      collatedBySupplier[supplierKey].fabricIds.push(fabric.fabric_id);
    });
    
    return collatedBySupplier;
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text, label = 'Content') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Copy supplier table as HTML
  const copySupplierTableHTML = (supplierName, items) => {
    // Gmail-compatible HTML with full inline styles
    let html = `<div style="font-family: Arial, sans-serif; margin: 20px 0;">
  <h3 style="color: #1e3a5f; font-size: 16px; font-weight: bold; margin-bottom: 12px; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px;">
    ${supplierName}
  </h3>
  <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; max-width: 600px; border-collapse: collapse; border: 1px solid #e2e8f0;">
    <thead>
      <tr style="background-color: #1e3a5f;">
        <th style="color: #ffffff; text-align: left; padding: 12px; font-weight: 600; font-size: 13px; border-right: 1px solid #334e68;">Customer/Group</th>
        <th style="color: #ffffff; text-align: left; padding: 12px; font-weight: 600; font-size: 13px; border-right: 1px solid #334e68;">Fabric SKU</th>
        <th style="color: #ffffff; text-align: right; padding: 12px; font-weight: 600; font-size: 13px;">Total Qty</th>
      </tr>
    </thead>
    <tbody>`;
    
    Object.values(items).forEach((item, index) => {
      const bgColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
      html += `
      <tr style="background-color: ${bgColor};">
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #1a202c; font-size: 13px;">${item.customer}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; color: #2d3748; font-family: 'Courier New', monospace; font-size: 13px;">${item.fabricSKU}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #1a202c; font-weight: 600; font-size: 13px;">${item.totalQty.toFixed(2)}</td>
      </tr>`;
    });
    
    html += `
    </tbody>
  </table>
</div>`;
    
    // Copy as HTML with proper formatting for email clients
    const blob = new Blob([html], { type: 'text/html' });
    const plainText = Object.values(items).map(item => 
      `${item.customer}\t${item.fabricSKU}\t${item.totalQty.toFixed(2)}`
    ).join('\n');
    
    // Copy both HTML and plain text for better compatibility
    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': blob,
        'text/plain': new Blob([plainText], { type: 'text/plain' })
      })
    ]).then(() => {
      toast.success(`${supplierName} table copied (HTML format for email)`);
    }).catch(err => {
      // Fallback to plain HTML copy
      copyToClipboard(html, `${supplierName} table`);
    });
  };

  // Copy individual row
  const copyRow = (customer, sku, qty) => {
    const rowText = `${customer}\t${sku}\t${qty.toFixed(2)}`;
    copyToClipboard(rowText, 'Row');
  };

  // Copy individual cell
  const copyCell = (text, label) => {
    copyToClipboard(text, label);
  };
  
  // Update procurement date and status for a supplier's fabrics
  const handleSupplierUpdate = async (supplierName, fabricIds) => {
    const updates = supplierUpdates[supplierName];
    if (!updates || (!updates.date && !updates.status)) {
      toast.error('Please set a date or status to update');
      return;
    }
    
    try {
      const updateData = {};
      if (updates.date) updateData.order_date = updates.date;
      if (updates.status) updateData.fabric_order_status = updates.status;
      
      // Update all fabric orders for this supplier using the inline update API
      await Promise.all(
        fabricIds.map(id => 
          inlineUpdateFabricOrder(id, updateData)
        )
      );
      
      toast.success(`Updated ${fabricIds.length} fabric order(s) for ${supplierName}`);
      
      // Refresh data without closing modal
      await fetchData({ page: 1, append: false });
    } catch (error) {
      console.error('Error updating fabrics:', error);
      toast.error('Failed to update fabric orders');
    }
  };

  // Sort handler
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Clear dates
  const clearDates = () => {
    setFromDate(null);
    setToDate(null);
  };

  // Toggle row expansion
  const toggleRowExpansion = (fabricId) => {
    const nextExpanded = expandedRow === fabricId ? null : fabricId;
    setExpandedRow(nextExpanded);
    if (nextExpanded) {
      ensureFabricDetail(nextExpanded);
    }
  };

  // Filter and sort fabric orders
  const filteredFabrics = useMemo(() => {
    let rows = fabricOrders.filter((fabric) => {
      if (filters.supplier !== 'all' && fabric.supplier_id !== filters.supplier) {
        return false;
      }
      if (filters.priority !== 'all' && fabric.priority !== filters.priority) {
        return false;
      }
      if (filters.status === 'received' && !RECEIVED_STATUS_VALUES.includes(fabric.fabric_order_status)) {
        return false;
      }
      if (filters.status !== 'all' && filters.status !== 'received' && fabric.fabric_order_status !== filters.status) {
        return false;
      }
      const alterationMeta = getAlterationMeta(fabric);
      if (filters.alterationType === 'alteration' && !alterationMeta.isAlteration) {
        return false;
      }
      if (filters.alterationType === 'standard' && alterationMeta.isAlteration) {
        return false;
      }
      return true;
    });

    if (sortConfig.key) {
      rows = [...rows].sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return rows;
  }, [fabricOrders, filters.supplier, filters.priority, filters.status, filters.alterationType, sortConfig.key, sortConfig.direction]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.supplier !== 'all') count += 1;
    if (filters.priority !== 'all') count += 1;
    const isDefaultNotOrderedStatus = quickView === 'not_ordered' && filters.status === 'not_ordered';
    if (filters.status !== 'all' && !isDefaultNotOrderedStatus) count += 1;
    if (filters.alterationType !== 'all') count += 1;
    if (searchTerm) count += 1;
    return count;
  }, [filters.supplier, filters.priority, filters.status, filters.alterationType, quickView, searchTerm]);

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="min-h-screen bg-[#f5f7fa] p-3" data-testid="fabric-procurement-page">
      <div className="max-w-[1600px] mx-auto">
        {/* Compact Header */}
        <div className="sticky top-0 z-20 mb-3 bg-[#f5f7fa]/95 backdrop-blur-sm border-b border-slate-200 pb-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-[#2d3748]">Fabric Orders Management</h1>
              <p className="text-xs text-[#718096]">
                {filteredFabrics.length} fabric order{filteredFabrics.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-3 flex flex-wrap md:flex-nowrap items-start md:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap md:flex-nowrap flex-1 min-w-0">
              <div className="relative w-full md:w-[360px] md:min-w-[320px]" data-testid="fabric-procurement-search-input-container">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#718096]" />
                <Input
                  ref={searchInputRef}
                  placeholder="Search orders, garments, SKU, supplier, customer... (/ to focus)"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  className="pl-10 bg-white border-[#e2e8f0] h-9"
                  data-testid="fabric-procurement-search-input"
                />
              </div>

              {/* Store Filter */}
              <div className="w-full md:w-[200px] md:flex-none" data-testid="fabric-procurement-store-filter-container">
                <Select value={storeFilter} onValueChange={setStoreFilter}>
                  <SelectTrigger className="w-full h-9 bg-white border-[#e2e8f0]" data-testid="fabric-procurement-store-filter">
                    <SelectValue placeholder="All Stores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stores</SelectItem>
                    {stores.map((store) => (
                      <SelectItem key={store.store_id} value={store.store_id}>
                        {store.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Indicator */}
            {(hasActiveFilters || searchTerm) && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-2 py-1">
                  <Filter className="w-3 h-3 mr-1" />
                  {activeFilterCount} active
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilters({ supplier: 'all', priority: 'all', status: 'all', alterationType: 'all' });
                    setQuickView('all');
                    setSearchTerm('');
                  }}
                  className="h-8 text-slate-600 hover:text-slate-900"
                  data-testid="fabric-procurement-clear-all-filters-button"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </div>

          <div className="mt-2 flex items-center gap-2" data-testid="fabric-procurement-quick-views">
            <Button
              variant={quickView === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyQuickView('all')}
              data-testid="fabric-procurement-quick-view-all"
            >
              All
            </Button>
            <Button
              variant={quickView === 'not_ordered' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyQuickView('not_ordered')}
              data-testid="fabric-procurement-quick-view-not-ordered"
            >
              Not Ordered
            </Button>
            <Button
              variant={quickView === 'checking_with_supplier' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyQuickView('checking_with_supplier')}
              data-testid="fabric-procurement-quick-view-checking-with-supplier"
            >
              Checking with Supplier
            </Button>
            <Button
              variant={quickView === 'in_transit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyQuickView('in_transit')}
              data-testid="fabric-procurement-quick-view-in-transit"
            >
              In Transit
            </Button>
            <Button
              variant={quickView === 'received' ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyQuickView('received')}
              data-testid="fabric-procurement-quick-view-received"
            >
              Received
            </Button>
          </div>
        </div>

      {/* Bulk Actions Card */}
      {selectedFabrics.length > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm text-muted-foreground">
                {selectedFabrics.length} fabric order{selectedFabrics.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => setCollationModalOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Summary
                </Button>
                <Button variant="outline" size="sm" onClick={() => setBulkStatusModalOpen(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Update Status
                </Button>
                <Button variant="outline" size="sm" onClick={() => setBulkEditModalOpen(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bulk Edit
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

        {/* Fabric Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredFabrics.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Package className="w-16 h-16 text-[#718096]/50 mb-4" />
              <h3 className="text-lg font-medium mb-2 text-[#2d3748]">No fabric orders found</h3>
              <p className="text-sm text-[#718096]">
                {searchTerm || hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Fabric orders will appear here when created from orders'}
              </p>
            </div>
          ) : (
            <div
              ref={tableScrollRef}
              onScroll={handleTableScroll}
              className="overflow-x-auto max-h-[calc(100vh-140px)]"
              data-testid="fabric-procurement-table-scroll-container"
            >
              <Table>
                <TableHeader className="sticky top-0 z-10 shadow-sm">
                  <TableRow className="bg-[#f8fafc] border-b-2 border-[#e2e8f0] hover:bg-[#f8fafc]">
                    <TableHead className="w-8 bg-[#f8fafc]"></TableHead>
                    <TableHead className="w-10 bg-[#f8fafc]">
                      <Checkbox
                        checked={selectedFabrics.length === filteredFabrics.length && filteredFabrics.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Order No</TableHead>
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                      <div className="flex items-center gap-1">
                        <span>Garment</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-5 w-5 p-0 rounded-sm ${
                                filters.alterationType !== 'all'
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                              }`}
                              data-testid="fabric-procurement-alteration-filter-trigger"
                            >
                              <Filter className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, alterationType: 'all' }))} data-testid="fabric-procurement-alteration-filter-all">
                              All Garments
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, alterationType: 'standard' }))} data-testid="fabric-procurement-alteration-filter-standard">
                              Standard Only
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, alterationType: 'alteration' }))} data-testid="fabric-procurement-alteration-filter-alt">
                              Alteration Only
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Customer</TableHead>
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Fabric SKU</TableHead>
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Quantity</TableHead>
                    
                    {/* Supplier with Filter Icon */}
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                      <div className="flex items-center gap-1">
                        <span>Supplier</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className={`h-5 w-5 p-0 rounded-sm ${
                                filters.supplier !== 'all' 
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              <Filter className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, supplier: 'all' }))}>
                              All Suppliers
                            </DropdownMenuItem>
                            {suppliers.map(s => (
                              <DropdownMenuItem key={s.supplier_id} onClick={() => setFilters(prev => ({ ...prev, supplier: s.supplier_id }))}>
                                {s.supplier_name}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableHead>
                    
                    {/* Priority with Filter Icon */}
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                      <div className="flex items-center gap-1">
                        <span>Priority</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className={`h-5 w-5 p-0 rounded-sm ${
                                filters.priority !== 'all' 
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              <Filter className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, priority: 'all' }))}>
                              All
                            </DropdownMenuItem>
                            {priorities.map(p => (
                              <DropdownMenuItem key={p.value} onClick={() => setFilters(prev => ({ ...prev, priority: p.value }))}>
                                {p.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableHead>
                    
                    {/* Status with Filter Icon */}
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">
                      <div className="flex items-center gap-1">
                        <span>Status</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className={`h-5 w-5 p-0 rounded-sm ${
                                filters.status !== 'all' 
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              <Filter className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => setFilters(prev => ({ ...prev, status: 'all' }))}>
                              All
                            </DropdownMenuItem>
                            {FABRIC_ORDER_STATUSES.map(s => (
                              <DropdownMenuItem key={s.value} onClick={() => setFilters(prev => ({ ...prev, status: s.value }))}>
                                {s.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableHead>
                    
                    <TableHead className="text-xs font-semibold text-[#2d3748] bg-[#f8fafc]">Procurement Dates</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {filteredFabrics.map((fabric) => {
                    const statusConfig = getStatusConfig(fabric.fabric_order_status);
                    const priorityConfig = getPriorityConfig(fabric.priority);
                    const isGrouped = !!fabric.parent_fabric_order_group_id;
                    const alterationMeta = getAlterationMeta(fabric);

                    return (
                      <Fragment key={fabric.fabric_id}>
                        {/* Main Fabric Row - Clickable to expand */}
                        <TableRow 
                          className="border-border hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={(e) => {
                            // Don't toggle if clicking on interactive elements
                            if (!e.target.closest('input, select, button, a')) {
                              toggleRowExpansion(fabric.fabric_id);
                            }
                          }}
                        >
                          {/* Chevron Icon */}
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRowExpansion(fabric.fabric_id);
                              }}
                            >
                              {expandedRow === fabric.fabric_id ? (
                                <ChevronDown className="w-4 h-4 text-slate-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </Button>
                          </TableCell>
                          
                          {/* Checkbox */}
                          <TableCell className="py-2">
                            <Checkbox
                              checked={selectedFabrics.includes(fabric.fabric_id)}
                              onCheckedChange={(checked) => handleSelectFabric(fabric.fabric_id, checked)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>

                          {/* Order Number Column */}
                          <TableCell>
                            <div className="flex flex-col items-start gap-0.5 min-w-0">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                                  {fabric.order_number}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 opacity-60 hover:opacity-100 shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(fabric.order_number);
                                    toast.success('Order number copied!');
                                  }}
                                  data-testid={`fabric-procurement-copy-order-${fabric.fabric_id}`}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              <Badge
                                variant="outline"
                                className={`text-[10px] px-1.5 py-0 ${getOrderStatusConfig(fabric.order_status).color}`}
                                data-testid={`fabric-procurement-order-status-badge-${fabric.fabric_id}`}
                              >
                                {getOrderStatusConfig(fabric.order_status).label}
                              </Badge>
                            </div>
                          </TableCell>

                          {/* Garment Column */}
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="font-mono text-xs font-semibold bg-primary/10 border-primary/30 text-primary w-fit">
                                  {extractGarmentSuffix(fabric.garment_no, fabric.order_number)}
                                </Badge>
                                {alterationMeta.isAlteration && (
                                  <Badge
                                    variant="outline"
                                    className="font-mono text-[10px] font-semibold bg-amber-50 border-amber-300 text-amber-700"
                                    data-testid={`fabric-procurement-alteration-badge-${fabric.fabric_id}`}
                                  >
                                    {alterationMeta.label}
                                  </Badge>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 opacity-60 hover:opacity-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(fabric.garment_no || '');
                                    toast.success('Garment number copied!');
                                  }}
                                  data-testid={`fabric-procurement-copy-garment-${fabric.fabric_id}`}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[10px] text-muted-foreground leading-tight">
                                  IDD: {fabric.internal_delivery_date ? formatDateWord(fabric.internal_delivery_date, 'Not set') : 'Not set'}
                                </span>
                                <span className="text-[10px] text-muted-foreground leading-tight">
                                  CDD: {fabric.client_final_date ? formatDateWord(fabric.client_final_date, 'Not set') : 'Not set'}
                                </span>
                              </div>
                            </div>
                          </TableCell>

                          {/* Customer */}
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{fabric.customer_name || '—'}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {formatCustomerNo(fabric.customer_no, fabric.customer_name)}
                              </p>
                            </div>
                          </TableCell>

                          {/* Fabric SKU */}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Input
                                value={fabric.fabric_sku || ''}
                                placeholder="Enter SKU..."
                                className="h-8 w-32 text-xs bg-transparent border-dashed"
                                onChange={(e) => handleInlineUpdate(fabric.fabric_id, 'fabric_sku', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`fabric-procurement-sku-input-${fabric.fabric_id}`}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 opacity-60 hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(fabric.fabric_sku || '');
                                  toast.success('Fabric SKU copied!');
                                }}
                                data-testid={`fabric-procurement-copy-sku-${fabric.fabric_id}`}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>

                          {/* Quantity */}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                step="0.1"
                                value={fabric.quantity || ''}
                                placeholder="Enter..."
                                className="h-8 w-20 text-xs bg-transparent border-dashed"
                                onChange={(e) => {
                                  // Update local state immediately for responsive UI
                                  setFabricOrders(prev => prev.map(f => 
                                    f.fabric_id === fabric.fabric_id ? { ...f, quantity: parseFloat(e.target.value) || '' } : f
                                  ));
                                }}
                                onBlur={(e) => {
                                  // Save to backend only when field is blurred
                                  handleInlineUpdate(fabric.fabric_id, 'quantity', parseFloat(e.target.value));
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-500 hover:text-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCalculator(fabric);
                                }}
                                title="Fabric calculator"
                              >
                                <Calculator className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>

                          {/* Supplier */}
                          <TableCell>
                            <Select
                              value={fabric.supplier_id || ''}
                              onValueChange={(v) => handleInlineUpdate(fabric.fabric_id, 'supplier_id', v)}
                            >
                              <SelectTrigger className="h-8 w-44 border-0 bg-transparent hover:bg-orange-100 text-xs">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {suppliers.map((s) => (
                                  <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>

                          {/* Priority */}
                          <TableCell>
                            <Select
                              value={fabric.priority || 'medium'}
                              onValueChange={(v) => handleInlineUpdate(fabric.fabric_id, 'priority', v)}
                            >
                              <SelectTrigger className="h-8 w-24 border-0 bg-transparent hover:bg-orange-100">
                                <Badge variant="outline" className={`${priorityConfig.color} border-0`}>
                                  {priorityConfig.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {priorities.map((p) => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Select
                              value={fabric.fabric_order_status || 'not_ordered'}
                              onValueChange={(v) => handleInlineUpdate(fabric.fabric_id, 'fabric_order_status', v)}
                            >
                              <SelectTrigger className="h-8 w-36 border-0 bg-transparent hover:bg-orange-100">
                                <Badge variant="outline" className={`${statusConfig.color} text-xs`}>
                                  {statusConfig.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {FABRIC_ORDER_STATUSES.map((s) => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>

                          {/* Procurement Dates (Combined) */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col gap-0.5">
                                <div className="text-[10px] text-slate-600">
                                  <span className="font-medium">Ordered:</span>{' '}
                                  <span className="text-slate-900">
                                    {fabric.order_date
                                      ? formatDateWord(fabric.order_date)
                                      : 'Not set'}
                                  </span>
                                </div>
                                <div className="text-[10px] text-slate-600">
                                  <span className="font-medium">Dispatched:</span>{' '}
                                  <span className="text-slate-900">
                                    {fabric.dispatched_date
                                      ? formatDateWord(fabric.dispatched_date)
                                      : 'Not set'}
                                  </span>
                                </div>
                                <div className="text-[10px] text-slate-600">
                                  <span className="font-medium">Received:</span>{' '}
                                  <span className="text-slate-900">
                                    {fabric.received_date
                                      ? formatDateWord(fabric.received_date)
                                      : 'Not set'}
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 opacity-60 hover:opacity-100 hover:bg-orange-100 hover:text-orange-700 flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDateModal(fabric);
                                }}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expandable Detail Row */}
                        {expandedRow === fabric.fabric_id && (
                          <TableRow className="bg-slate-50 border-t-0">
                            <TableCell colSpan={12} className="py-4 px-8">
                              {detailsLoadingById[fabric.fabric_id] ? (
                                <div className="py-6 text-sm text-slate-500" data-testid={`fabric-detail-loading-${fabric.fabric_id}`}>
                                  Loading fabric details...
                                </div>
                              ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Left Column: Order Information */}
                                <div>
                                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Order Information</h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-600">Client Delivery:</span>
                                      <span className="font-medium text-slate-900">
                                        {fabric.client_final_date ? formatDateWord(fabric.client_final_date, 'Not set') : 'Not set'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-600">Internal Delivery:</span>
                                      <span className="font-medium text-slate-900">
                                        {fabric.internal_delivery_date ? formatDateWord(fabric.internal_delivery_date, 'Not set') : 'Not set'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-600">Test Fitting:</span>
                                      <span className="font-medium text-slate-900">
                                        {fabric.test_fit_date ? formatDateWord(fabric.test_fit_date, 'Not set') : 'Not set'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* View Customizations Button */}
                                  {fabric.customizations && fabric.customizations.length > 0 && (
                                    <div className="mt-4">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await handleViewCustomizations(fabric);
                                        }}
                                        className="h-8 text-xs"
                                      >
                                        <Eye className="w-3.5 h-3.5 mr-2" />
                                        View Customizations
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Right Column: Notes */}
                                <div>
                                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Notes</h4>
                                  <Textarea
                                    value={fabric.notes || ''}
                                    onChange={(e) => {
                                      const updatedFabric = { ...fabric, notes: e.target.value };
                                      setFabricOrders(prev => prev.map(f => f.fabric_id === fabric.fabric_id ? updatedFabric : f));
                                    }}
                                    onBlur={(e) => {
                                      handleInlineUpdate(fabric.fabric_id, 'notes', e.target.value);
                                    }}
                                    placeholder="Add notes about this fabric order..."
                                    className="min-h-[120px] text-sm bg-white border-slate-200 resize-none"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              {loadingMore && (
                <div className="p-4 space-y-2" data-testid="fabric-procurement-loading-more-indicator">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}
              {!loadingMore && !hasMorePages && filteredFabrics.length > 0 && (
                <div className="py-3 text-center text-xs text-slate-500" data-testid="fabric-procurement-end-of-list">
                  End of results
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk Status Update Modal */}
        <Dialog open={bulkStatusModalOpen} onOpenChange={setBulkStatusModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>
              Update the status for {selectedFabrics.length} selected fabric order(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={bulkStatusValue} onValueChange={setBulkStatusValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {FABRIC_ORDER_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusModalOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkStatusUpdate}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Modal */}
      <Dialog open={bulkEditModalOpen} onOpenChange={setBulkEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Edit</DialogTitle>
            <DialogDescription>
              Apply changes to {selectedFabrics.length} selected fabric order(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select value={bulkEditSupplier} onValueChange={setBulkEditSupplier}>
                <SelectTrigger>
                  <SelectValue placeholder="Don't change" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={bulkEditPriority} onValueChange={setBulkEditPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="Don't change" />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={bulkEditNotes}
                onChange={(e) => setBulkEditNotes(e.target.value)}
                placeholder="Add notes to all selected..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkEdit}>Apply Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Fabric Order Details</DialogTitle>
          </DialogHeader>
          {selectedFabricForDetails && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Order Number</Label>
                  <p className="font-mono">{selectedFabricForDetails.order_number || '—'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Garment</Label>
                  <Badge variant="outline" className="font-mono text-xs font-semibold bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
                    {extractGarmentSuffix(selectedFabricForDetails.garment_no, selectedFabricForDetails.order_number)}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Customer</Label>
                  <p>{selectedFabricForDetails.customer_name || '—'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Store</Label>
                  <p>{selectedFabricForDetails.store || '—'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Order Status</Label>
                  <Badge variant="outline" className={getOrderStatusConfig(selectedFabricForDetails.order_status).color}>
                    {getOrderStatusConfig(selectedFabricForDetails.order_status).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Fabric SKU</Label>
                  <p>{selectedFabricForDetails.fabric_sku || '—'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Fabric Type</Label>
                  <p className="capitalize">{selectedFabricForDetails.fabric_type?.replace('_', ' ') || '—'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Quantity</Label>
                  <p>{selectedFabricForDetails.quantity}m</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Supplier</Label>
                  <p>{selectedFabricForDetails.supplier_name || '—'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Priority</Label>
                  <Badge variant="outline" className={getPriorityConfig(selectedFabricForDetails.priority).color}>
                    {getPriorityConfig(selectedFabricForDetails.priority).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Status</Label>
                  <Badge variant="outline" className={getStatusConfig(selectedFabricForDetails.fabric_order_status).color}>
                    {getStatusConfig(selectedFabricForDetails.fabric_order_status).label}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Notes</Label>
                <Textarea
                  value={selectedFabricForDetails.notes || ''}
                  onChange={(e) => {
                    setSelectedFabricForDetails({ ...selectedFabricForDetails, notes: e.target.value });
                  }}
                  onBlur={() => {
                    handleInlineUpdate(selectedFabricForDetails.fabric_id, 'notes', selectedFabricForDetails.notes);
                  }}
                  placeholder="Add notes..."
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customizations Dialog */}
      <Dialog open={customizationsOpen} onOpenChange={setCustomizationsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Design Customizations</DialogTitle>
            <DialogDescription className="text-sm">
              {selectedFabricForCustomizations?.garment_no} - {selectedFabricForCustomizations?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedFabricForCustomizations?.customizations && selectedFabricForCustomizations.customizations.length > 0 ? (
            <div className="space-y-4 mt-4">
              {/* Sort and group customizations by category using snapshot schema */}
              {(() => {
                const customizations = selectedFabricForCustomizations.customizations;
                const schemaToUse = selectedFabricForCustomizations.snapshot_customization_schema?.categories;
                
                // Sort customizations using the same logic as LineItemRow
                const sortedCustomizations = sortCustomizations(customizations, schemaToUse);
                
                // Group by category while preserving category metadata
                const grouped = sortedCustomizations.reduce((acc, c) => {
                  const categoryName = c.category_name || 'General';
                  const category = schemaToUse?.find(cat => cat.name === categoryName);
                  
                  if (!acc[categoryName]) {
                    acc[categoryName] = {
                      name: categoryName,
                      display_order: category?.display_order ?? 999,
                      options: []
                    };
                  }
                  acc[categoryName].options.push(c);
                  return acc;
                }, {});
                
                // Convert to array and sort by display_order
                const groupedCustomizations = Object.values(grouped).sort((a, b) => a.display_order - b.display_order);
                
                return groupedCustomizations.map((categoryGroup) => (
                  <div key={categoryGroup.name} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <h4 className="font-semibold text-sm text-slate-900 mb-3 uppercase tracking-wide">{categoryGroup.name}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                      {categoryGroup.options.map((option, optIdx) => (
                        <div key={optIdx} className="flex items-baseline gap-2">
                          <span className="text-sm text-slate-600 min-w-[140px]">{option.option_name}:</span>
                          <span className="text-sm font-medium text-slate-900">
                            {option.selected_value}
                            {option.price_impact > 0 && (
                              <span className="text-orange-600 ml-1 text-xs">(+${option.price_impact})</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">
              <p className="text-sm">No customizations available for this fabric order</p>
            </div>
          )}
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setCustomizationsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Fabric Calculator Modal */}
      <FabricCalculatorModal
        open={calculatorOpen}
        onOpenChange={setCalculatorOpen}
        fabricOrder={selectedFabricForCalculator}
        onConfirm={handleCalculatorConfirm}
      />
      
      {/* Date Edit Modal */}
      <Dialog open={dateEditModalOpen} onOpenChange={setDateEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Procurement Dates</DialogTitle>
            <DialogDescription>
              Update the procurement timeline dates for this fabric order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Order Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Order Date (Procurement Date)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Date when the fabric order was placed with the supplier
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editingDates.order_date
                      ? formatDateWord(editingDates.order_date)
                      : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editingDates.order_date ? new Date(editingDates.order_date) : undefined}
                    onSelect={(date) => {
                      setEditingDates(prev => ({
                        ...prev,
                        order_date: date ? format(date, 'yyyy-MM-dd') : null
                      }));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Dispatch Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Dispatch Date</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Date when the fabric was dispatched from the supplier
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editingDates.dispatched_date
                      ? formatDateWord(editingDates.dispatched_date)
                      : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editingDates.dispatched_date ? new Date(editingDates.dispatched_date) : undefined}
                    onSelect={(date) => {
                      setEditingDates(prev => ({
                        ...prev,
                        dispatched_date: date ? format(date, 'yyyy-MM-dd') : null
                      }));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Received Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Received Date</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Date when the fabric was received at your facility
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editingDates.received_date
                      ? formatDateWord(editingDates.received_date)
                      : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editingDates.received_date ? new Date(editingDates.received_date) : undefined}
                    onSelect={(date) => {
                      setEditingDates(prev => ({
                        ...prev,
                        received_date: date ? format(date, 'yyyy-MM-dd') : null
                      }));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDates}>
              Save Dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Collation Summary Modal - Grouped by Supplier */}
      <Dialog open={collationModalOpen} onOpenChange={setCollationModalOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fabric Order Summary by Supplier</DialogTitle>
            <DialogDescription>
              {selectedFabrics.length} items selected - Grouped by supplier, then collated by customer and fabric
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Toggle for grouping */}
            <div className="flex items-center bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <Checkbox 
                id="group-by-parent" 
                checked={groupByParent}
                onCheckedChange={setGroupByParent}
              />
              <Label htmlFor="group-by-parent" className="ml-2 cursor-pointer text-slate-700">
                Group by Parent Order (combine child orders under group name)
              </Label>
            </div>
            
            {/* Per-Supplier Tables */}
            {(() => {
              const collatedBySupplier = generateCollationSummary();
              const supplierEntries = Object.entries(collatedBySupplier);
              
              if (supplierEntries.length === 0) {
                return (
                  <div className="text-center text-muted-foreground py-8">
                    No items to display
                  </div>
                );
              }
              
              return supplierEntries.map(([supplierName, supplierData]) => {
                const isNotUpdated = supplierName === 'Not Updated';
                
                return (
                  <div key={supplierName} className="border border-slate-200 rounded-lg p-4 space-y-3 bg-white">
                    {/* Supplier Header */}
                    <div className={`flex items-center justify-between pb-2 border-b ${isNotUpdated ? 'border-amber-300' : 'border-slate-300'}`}>
                      <h3 className={`font-semibold text-lg ${isNotUpdated ? 'text-amber-700' : 'text-slate-900'}`}>
                        {isNotUpdated && <span className="mr-2">⚠️</span>}
                        {supplierName}
                        {isNotUpdated && <span className="ml-2 text-sm font-normal text-amber-600">(Supplier Not Set)</span>}
                      </h3>
                      <Badge variant="outline" className="bg-white text-slate-700 border-slate-300">{Object.keys(supplierData.items).length} items</Badge>
                    </div>
                    
                    {/* Data Table */}
                    <div className="border border-slate-200 rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-100">
                            <TableHead className="font-semibold text-slate-800">Customer/Group</TableHead>
                            <TableHead className="font-semibold text-slate-800">Fabric SKU</TableHead>
                            <TableHead className="font-semibold text-slate-800 text-right">Total Qty</TableHead>
                            <TableHead className="w-20"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.values(supplierData.items).map((item, index) => (
                            <TableRow key={index} className="hover:bg-slate-50">
                              <TableCell 
                                className="font-medium text-slate-900 cursor-pointer hover:bg-slate-100"
                                onClick={() => copyCell(item.customer, 'Customer')}
                                title="Click to copy"
                              >
                                {item.customer}
                              </TableCell>
                              <TableCell 
                                className="font-mono text-sm text-slate-800 cursor-pointer hover:bg-slate-100"
                                onClick={() => copyCell(item.fabricSKU, 'Fabric SKU')}
                                title="Click to copy"
                              >
                                {item.fabricSKU}
                              </TableCell>
                              <TableCell 
                                className="text-right font-semibold text-slate-900 cursor-pointer hover:bg-slate-100"
                                onClick={() => copyCell(item.totalQty.toFixed(2), 'Quantity')}
                                title="Click to copy"
                              >
                                {item.totalQty.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => copyRow(item.customer, item.fabricSKU, item.totalQty)}
                                  title="Copy entire row"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Update Controls for this Supplier */}
                    <div className="bg-blue-50 p-3 rounded-md space-y-3 border border-blue-200">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <Label className="text-xs text-muted-foreground mb-1 block">Procurement Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full justify-start text-left font-normal">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {supplierUpdates[supplierName]?.date ? formatDateWord(supplierUpdates[supplierName].date, 'Select date') : 'Select date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[100]">
                              <Calendar
                                mode="single"
                                selected={supplierUpdates[supplierName]?.date ? new Date(supplierUpdates[supplierName].date) : undefined}
                                onSelect={(date) => {
                                  setSupplierUpdates(prev => ({
                                    ...prev,
                                    [supplierName]: {
                                      ...prev[supplierName],
                                      date: date ? format(date, 'yyyy-MM-dd') : null
                                    }
                                  }));
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        <div className="flex-1 min-w-[200px]">
                          <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
                          <Select
                            value={supplierUpdates[supplierName]?.status || ''}
                            onValueChange={(value) => {
                              setSupplierUpdates(prev => ({
                                ...prev,
                                [supplierName]: {
                                  ...prev[supplierName],
                                  status: value
                                }
                              }));
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent className="z-[100]">
                              {FABRIC_ORDER_STATUSES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <Button 
                          size="sm"
                          onClick={() => handleSupplierUpdate(supplierName, supplierData.fabricIds)}
                          className="mt-5"
                        >
                          Apply to {supplierData.fabricIds.length} item(s)
                        </Button>
                      </div>
                    </div>
                    
                    {/* Copy Button for this Supplier */}
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => copySupplierTableHTML(supplierName, supplierData.items)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy This Supplier&apos;s Table (HTML Format)
                    </Button>
                  </div>
                );
              });
            })()}
            
            <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md border border-slate-200">
              <strong className="text-slate-800">Tips:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Click any cell to copy its value</li>
                <li>Use row copy button to copy entire row</li>
                <li>Copy supplier table in HTML format - paste directly into email body</li>
                <li>Set procurement date and status per supplier, then apply updates</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollationModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default FabricProcurementPage;
